import { Check, Lock, Plus, X } from '@/components/ui/icons';
import { useRef, useState } from 'react';
import { canGrantPermissions, isPrivateChannel, Permissions, type ChannelDTO, type ChannelOverwrite, type PermissionName, type RoleDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { channelApi } from '@/api/endpoints';
import { Switch } from '@/components/ui/Input';
import { Menu } from '@/components/ui/Popover';
import { useChannelPermissions } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useServerRoles, useServers } from '@/stores/servers';
import { toast } from '@/stores/ui';

const VIEW = Permissions.VIEW_CHANNEL;

const TEXT_PERMISSIONS: Array<{ key: PermissionName; label: string }> = [
  { key: 'VIEW_CHANNEL', label: 'See the channel' },
  { key: 'SEND_MESSAGES', label: 'Send messages' },
  { key: 'ADD_REACTIONS', label: 'Add reactions' },
  { key: 'ATTACH_FILES', label: 'Attach files' },
  { key: 'MANAGE_MESSAGES', label: 'Manage messages (delete and pin)' },
];
const VOICE_PERMISSIONS: Array<{ key: PermissionName; label: string }> = [
  { key: 'VIEW_CHANNEL', label: 'See the channel' },
  { key: 'CONNECT', label: 'Connect' },
  { key: 'SPEAK', label: 'Speak' },
];

const dot = (role: RoleDTO) => <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: role.color ?? 'var(--clanfire-fg-muted)' }} />;
const roleLabel = (role: RoleDTO) => (role.isDefault ? `${role.name} (everyone)` : role.name);

/**
 * Who can see and use a channel: a "private" switch with the roles that can see
 * it, and per-role allow / default / deny settings for the channel's permissions.
 */
export function ChannelPermissions({ channel }: { channel: ChannelDTO }) {
  const roles = useServerRoles(channel.serverId);
  const perms = useChannelPermissions(channel.id);
  const defaultRole = roles.find((r) => r.isDefault);
  const [selectedId, setSelectedId] = useState<string | null>(defaultRole?.id ?? null);
  const [busy, setBusy] = useState(false);
  const addRef = useRef<HTMLButtonElement>(null);
  const [addOpen, setAddOpen] = useState(false);

  if (!perms.can('MANAGE_ROLES')) {
    return <p className="text-sm text-fg-muted">You need the Manage Roles permission to change who can see and use this channel.</p>;
  }

  const overwriteOf = (roleId: string): ChannelOverwrite => channel.overwrites.find((o) => o.roleId === roleId) ?? { roleId, allow: 0, deny: 0 };
  const editable = (role: RoleDTO) => perms.isOwner || role.isDefault || role.position < perms.rank;
  const isPrivate = isPrivateChannel(channel.overwrites, defaultRole?.id);

  const save = async (roleId: string, allow: number, deny: number) => {
    setBusy(true);
    try {
      useServers.getState().upsertChannel(await channelApi.setOverwrite(channel.id, roleId, { allow, deny }));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  /** Sets one permission for a role to allowed, denied or (null) the role's normal setting. */
  const setBit = (roleId: string, bit: number, state: 'allow' | 'deny' | null) => {
    const o = overwriteOf(roleId);
    const allow = state === 'allow' ? o.allow | bit : o.allow & ~bit;
    const deny = state === 'deny' ? o.deny | bit : o.deny & ~bit;
    void save(roleId, allow, deny);
  };

  const setPrivate = (on: boolean) => {
    if (defaultRole) setBit(defaultRole.id, VIEW, on ? 'deny' : null);
  };

  // Roles (other than everyone) that can see a private channel.
  const viewers = roles.filter((r) => !r.isDefault && (overwriteOf(r.id).allow & VIEW) !== 0);
  const canAddViewer = roles.filter((r) => !r.isDefault && editable(r) && !viewers.includes(r));
  // Roles with any override, for the detailed list (everyone always first).
  const listed = roles.filter((r) => r.isDefault || channel.overwrites.some((o) => o.roleId === r.id) || r.id === selectedId);
  const unlisted = roles.filter((r) => !listed.includes(r) && editable(r));
  const selected = roles.find((r) => r.id === selectedId) ?? defaultRole;
  const permissionRows = channel.type === 'VOICE' ? VOICE_PERMISSIONS : TEXT_PERMISSIONS;

  return (
    <div className="flex flex-col gap-5">
      <section>
        <Switch
          checked={isPrivate}
          onChange={setPrivate}
          disabled={busy || !defaultRole || !canGrantPermissions(perms.permissions, VIEW)}
          label="Private channel"
          description="Only the roles you choose (plus Administrators and the owner) can see it."
        />
        {isPrivate && (
          <div className="rounded-xl border border-line bg-sunken p-3">
            <h3 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold tracking-wider text-fg-muted uppercase">
              <Lock size={12} /> Who can see it
            </h3>
            <ul className="flex flex-wrap items-center gap-1.5" aria-label="Roles that can see this channel">
              {viewers.length === 0 && <li className="text-sm text-fg-muted">Only Administrators and the owner, so far.</li>}
              {viewers.map((role) => (
                <li key={role.id} className="inline-flex items-center gap-1.5 rounded-md bg-hover py-1 pr-2 pl-2 text-xs font-semibold">
                  {dot(role)}
                  {role.name}
                  {editable(role) && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setBit(role.id, VIEW, null)}
                      aria-label={`Stop ${role.name} seeing this channel`}
                      className="-mr-1 rounded p-0.5 text-fg-muted hover:bg-danger hover:text-white disabled:opacity-50"
                    >
                      <X size={12} />
                    </button>
                  )}
                </li>
              ))}
              {canAddViewer.length > 0 && (
                <li>
                  <button
                    ref={addRef}
                    type="button"
                    disabled={busy}
                    onClick={() => setAddOpen((v) => !v)}
                    aria-label="Let a role see this channel"
                    aria-haspopup="menu"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-hover text-fg-muted hover:bg-accent hover:text-accent-fg disabled:opacity-50"
                  >
                    <Plus size={14} />
                  </button>
                  <Menu
                    open={addOpen}
                    onClose={() => setAddOpen(false)}
                    anchor={addRef}
                    label="Let a role see this channel"
                    items={canAddViewer.map((role) => ({ label: role.name, icon: dot(role), onSelect: () => setBit(role.id, VIEW, 'allow') }))}
                  />
                </li>
              )}
            </ul>
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-1 text-[11px] font-bold tracking-wider text-fg-muted uppercase">Permissions by role</h3>
        <p className="mb-3 text-sm text-fg-muted">
          Change what a role can do in this channel only. <strong>Default</strong> keeps the role's normal setting.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex shrink-0 flex-col gap-1 sm:w-44">
            {listed.map((role) => (
              <button
                key={role.id}
                type="button"
                onClick={() => setSelectedId(role.id)}
                aria-pressed={selected?.id === role.id}
                className={cn('flex items-center gap-2 rounded-full px-3 py-1.5 text-left text-sm', selected?.id === role.id ? 'bg-accent-soft font-semibold text-fg' : 'text-fg-soft hover:bg-hover')}
              >
                {dot(role)}
                <span className="truncate">{roleLabel(role)}</span>
              </button>
            ))}
            {unlisted.length > 0 && <AddRoleButton roles={unlisted} onPick={(role) => setSelectedId(role.id)} />}
          </div>
          {selected && (
            <div className="min-w-0 flex-1 rounded-xl border border-line bg-sunken p-3">
              {!editable(selected) && <p className="mb-2 text-sm text-warning">You can only change roles below your own.</p>}
              <ul className="flex flex-col divide-y divide-line">
                {permissionRows.map((p) => {
                  const bit = Permissions[p.key];
                  const o = overwriteOf(selected.id);
                  const state = o.allow & bit ? 'allow' : o.deny & bit ? 'deny' : null;
                  const disabled = busy || !editable(selected) || !canGrantPermissions(perms.permissions, bit);
                  return (
                    <li key={p.key} className="flex items-center justify-between gap-3 py-2">
                      <span className="text-sm text-fg">{p.label}</span>
                      <TriState label={`${p.label} for ${selected.name}`} value={state} disabled={disabled} onChange={(next) => setBit(selected.id, bit, next)} />
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function AddRoleButton({ roles, onPick }: { roles: RoleDTO[]; onPick: (role: RoleDTO) => void }) {
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <button ref={ref} type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" className="flex items-center gap-2 rounded-full px-3 py-1.5 text-left text-sm text-fg-muted hover:bg-hover hover:text-fg">
        <Plus size={14} /> Add a role
      </button>
      <Menu open={open} onClose={() => setOpen(false)} anchor={ref} label="Add a role" items={roles.map((role) => ({ label: role.name, icon: dot(role), onSelect: () => onPick(role) }))} />
    </>
  );
}

/** Deny / default / allow for one permission. */
function TriState({ label, value, disabled, onChange }: { label: string; value: 'allow' | 'deny' | null; disabled: boolean; onChange: (value: 'allow' | 'deny' | null) => void }) {
  const options = [
    { value: 'deny' as const, title: 'Deny', icon: <X size={14} />, active: 'bg-danger text-white' },
    { value: null, title: 'Default', icon: <span className="text-sm leading-none font-bold">/</span>, active: 'bg-line-strong text-fg' },
    { value: 'allow' as const, title: 'Allow', icon: <Check size={14} />, active: 'bg-success text-white' },
  ];
  return (
    <div role="radiogroup" aria-label={label} className="flex shrink-0 overflow-hidden rounded-lg border border-line">
      {options.map((o) => (
        <button
          key={o.title}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          aria-label={o.title}
          title={o.title}
          disabled={disabled}
          onClick={() => onChange(o.value)}
          className={cn('flex h-7 w-8 items-center justify-center transition-colors disabled:opacity-50', value === o.value ? o.active : 'text-fg-muted hover:bg-hover')}
        >
          {o.icon}
        </button>
      ))}
    </div>
  );
}
